import { Fragment } from "react";
import { isEmpty, pick, some, truncate, values } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-regular-svg-icons";
import { Location } from "../../../@types/appointments";
import AppointmentLocationMap from "./AppointmentLocationMap.tsx";

// Skipping the character-based truncate lets a caller show the full link and
// truncate it visually instead (CSS ellipsis + a title tooltip), see the
// `truncateLink` prop below.
// A URL has no place the browser may break at, so it breaks wherever the line
// happens to end, mid-word. A zero-width hint after every slash moves the
// break to where a reader expects it (tixxt/core#474).
export function withBreakHints(text: string) {
  const parts = text.split("/");

  return parts.map((part, index) => (
    <Fragment key={index}>
      {part}
      {index < parts.length - 1 && (
        <>
          {"/"}
          <wbr />
        </>
      )}
    </Fragment>
  ));
}

export function getLocationLinkText(location: Location, truncate65 = true) {
  switch (location.meeting_link_type) {
    case "ms_teams":
      return I18n.t("js.calendars.appointment.meeting.link_text.ms_teams");
    case "google_meet":
      return I18n.t("js.calendars.appointment.meeting.link_text.google_meet");
    case "zoom":
      return I18n.t("js.calendars.appointment.meeting.link_text.zoom");
    default:
      return truncate65 && location.link && location.link?.length > 65
        ? truncate(location.link, { length: 65 })
        : location.link;
  }
}

export default function AppointmentLocation(
  location: Location & {
    showMap?: boolean;
    // Renders the location link as a single truncated line with the full
    // link available on hover, instead of wrapping/cutting it at a fixed
    // character count (used by the channel widget, where the card is much
    // narrower than the appointment detail page).
    truncateLink?: boolean;
  },
) {
  const locationIsExtended = some(
    ["address1", "address2", "zip", "city", "link", "country"],
    (key) => !isEmpty(location[key]),
  );

  const addressForUri = encodeURIComponent(
    values(
      pick(location, ["address1", "address2", "zip", "city", "country"]),
    ).join(" "),
  );

  return (
    <>
      {(!isEmpty(location.name) || locationIsExtended) && (
        <div className="detail-location detail-block flex gap-2 min-w-0">
          <FontAwesomeIcon className="detail-icon" icon={faLocationDot} />
          {/* A pasted URL has no space to break at, so the box has to be
              allowed to shrink below it (min-w-0) before `break-words` can do
              its work. Together they keep a long location inside the box, in
              the card and on a phone, and the break lands on a slash wherever
              the next segment still fits (tixxt/core#474). */}
          <address className="min-w-0 break-words">
            <>
              {location.name && (
                <div className="font-bold">{withBreakHints(location.name)}</div>
              )}
              {locationIsExtended && (
                <>
                  {location.address1 && <div>{location.address1}</div>}
                  {location.address2 && <div>{location.address2}</div>}
                  {(location.zip || location.city) && (
                    <div>{`${location.zip} ${location.city}`}</div>
                  )}
                  {location.country && <div>{location.country}</div>}
                  {location.link && (
                    <div
                      className={location.truncateLink ? "min-w-0" : undefined}
                    >
                      <a
                        href={location.link}
                        target="_blank"
                        rel="nofollow noreferrer"
                        // Only needed when the visible text is CSS-clipped
                        // (truncateLink); the classic wrapping link already
                        // shows the full text, so no tooltip is required.
                        title={
                          location.truncateLink ? location.link : undefined
                        }
                        className={
                          location.truncateLink ? "block truncate" : undefined
                        }
                      >
                        {withBreakHints(
                          getLocationLinkText(
                            location,
                            !location.truncateLink,
                          ) || "",
                        )}
                      </a>
                    </div>
                  )}
                </>
              )}
            </>
          </address>
        </div>
      )}
      {location.maps_url && location.showMap && (
        <AppointmentLocationMap
          url={location.maps_url}
          address={addressForUri}
        />
      )}
    </>
  );
}
